import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { homeLabel } from "@/lib/home";
import { useCachedState } from "@/lib/swr";
import { brandAccent } from "@/lib/brand";
import { useDash } from "./ctx";
import { V, useVocab } from "@/lib/vocab";
import { GettingStartedCard } from "@/components/GettingStartedCard";
import { RangePicker, windowLabel, windowTitle } from "@/components/RangePicker";
import { AskedAndAfterHours } from "@/components/AskedAndAfterHours";
import { PulseCard } from "@/components/pulse/PulseCard";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import {
  AlertTriangle, ClipboardList, HelpCircle, Users,
  TrendingUp, CheckCircle2, ThumbsUp, ThumbsDown,
  Lightbulb, ChevronRight, Gauge, MessageSquareDashed, MessageSquareWarning,
  Sparkles, UserPlus, PhoneCall, MessageSquare, ShieldCheck,
} from "lucide-react";

// Design tokens (dense, light, Stripe/Mixpanel-style)
const LINE = "#EDE8E0";
const CARD = "bg-white rounded-2xl border border-[#E5E0D8] shadow-[0_1px_2px_rgba(0,0,0,0.04)]";
const EYEBROW = "text-[11px] font-bold uppercase tracking-[0.14em] text-[#8A8378]";

const SENTIMENT = {
  positive: { label: "Positive", cls: "text-emerald-600", dot: "bg-emerald-500", ring: "ring-emerald-500/25" },
  mixed: { label: "Mixed", cls: "text-amber-600", dot: "bg-amber-500", ring: "ring-amber-400/25" },
  negative: { label: "Needs attention", cls: "text-redline-600", dot: "bg-redline-500", ring: "ring-redline-500/25" },
  neutral: { label: "Not enough data", cls: "text-[#A8A29E]", dot: "bg-sand-300", ring: "ring-sand-300/40" },
};

const sentBasisFactory = () => ({
  blend: { chip: "Calls + texts", title: "AI-scored phone calls blended with the tone of texts, weighted by how many of each came in" },
  ai_calls: { chip: "AI-scored calls", title: "Scored by AI from real call transcripts — was the need met and how did the caller feel" },
  keywords: { chip: "Text tone", title: `Read from positive and negative wording in ${V.lcs("person")}' texts` },
});

// A hairline-divided metric strip (dividers come from the gap showing through).
// Cells may carry onClick (→ button), help (hover tooltip) and tone ("alert" turns the value red).
function MetricStrip({ cols, cells, testidPrefix }) {
  return (
    <div className={`${CARD} overflow-hidden`}>
      <div className={`grid grid-cols-2 sm:grid-cols-3 ${cols} gap-px bg-[#EDE8E0]`}>
        {cells.map((c) => {
          const Tag = c.onClick ? "button" : "div";
          return (
            <Tag key={c.key} type={c.onClick ? "button" : undefined} onClick={c.onClick} title={c.help}
              data-testid={testidPrefix ? `${testidPrefix}${c.key}` : undefined}
              className={`bg-white px-5 py-4 text-left ${c.onClick ? "hover:bg-[#FAF8F4] transition-colors group" : ""}`}>
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#8A8378]">
                <c.icon className={`h-3.5 w-3.5 ${c.iconCls}`} strokeWidth={2} /> <span className="truncate">{c.label}</span>
                {c.help && <HelpCircle className="h-3 w-3 text-[#C9C3B8] ml-auto shrink-0" strokeWidth={2} />}
              </div>
              <div className={`font-heading text-2xl md:text-3xl font-bold tracking-tight mt-2 leading-none tabular-nums ${c.tone === "alert" ? "text-redline-600" : "text-[#1C1917]"}`}>{c.value}</div>
              {c.sub && <div className="text-[11px] text-[#A8A29E] mt-1 truncate">{c.sub}</div>}
            </Tag>
          );
        })}
      </div>
    </div>
  );
}

const CALLS_COLOR = "#1C1917";

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const by = Object.fromEntries(payload.map((p) => [p.dataKey, p]));
  const texts = by.texts?.value ?? payload[0].value;
  const calls = by.calls?.value ?? 0;
  const row = payload[0]?.payload || {};
  const dateLabel = row.date
    ? new Date(row.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) + (row.weekly ? " · week of" : "")
    : label;
  return (
    <div className={`bg-white border border-[#E5E0D8] px-3.5 py-2.5 rounded-xl shadow-[0_10px_40px_rgb(0,0,0,0.12)]`} data-testid="volume-tooltip">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-[#A8A29E]">{dateLabel}</div>
      <div className="flex items-baseline gap-3 mt-0.5">
        <div className="text-lg font-black leading-tight" style={{ color: by.texts?.color || "#1C1917" }}>
          {texts} <span className="text-xs font-semibold text-[#78716C]">texts</span>
        </div>
        <div className="text-lg font-black leading-tight" style={{ color: CALLS_COLOR }}>
          {calls} <span className="text-xs font-semibold text-[#78716C]">calls</span>
        </div>
      </div>
    </div>
  );
}

export default function Overview() {
  const SENT_BASIS = sentBasisFactory();
  const { tenantId, tenant, role } = useDash();
  const v = useVocab();
  const navigate = useNavigate();
  const accent = brandAccent();
  const [params, setParams] = useSearchParams();
  const rangeQ = { from: params.get("from") || undefined, to: params.get("to") || undefined, days: params.get("days") || undefined };
  const rangeKey = `${rangeQ.from || ""}|${rangeQ.to || ""}|${rangeQ.days || ""}`;
  const [data, setData] = useCachedState(`ov:${tenantId}:${rangeKey}`, null);
  const [pulse, setPulse] = useCachedState(`ov-pulse:${tenantId}`, null);
  const [sugCount, setSugCount] = useCachedState(`ov-sug:${tenantId}`, 0);
  const [showAllEsc, setShowAllEsc] = useState(false);
  const [showAllReq, setShowAllReq] = useState(false);
  const [sentHelp, setSentHelp] = useState(false);
  const CAP = 8;

  useEffect(() => {
    if (!tenantId) return;
    let alive = true;
    api.get(`/tenants/${tenantId}/analytics`, { params: rangeQ }).then((r) => { if (alive) setData(r.data); }).catch(() => { if (alive) setData(null); });
    api.get(`/tenants/${tenantId}/suggestions`).then((r) => { if (alive) setSugCount((r.data || []).length); }).catch(() => { if (alive) setSugCount(0); });
    // Voice + Text pulse refreshes every 30s so live-now feels live without hammering the DB.
    const loadPulse = () => api.get(`/tenants/${tenantId}/pulse`).then((r) => { if (alive) setPulse(r.data); }).catch(() => {});
    loadPulse();
    const iv = setInterval(loadPulse, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, [tenantId, rangeKey]); // eslint-disable-line

  const setRange = (next) => {
    const p = new URLSearchParams(params);
    ["from", "to", "days"].forEach((k) => p.delete(k));
    Object.entries(next).forEach(([k, val]) => { if (val) p.set(k, String(val)); });
    setParams(p, { replace: true });
  };

  if (!data) return null;
  const win = data.window || {};
  const winLabel = windowLabel(win);              // "Aug 13 – Sep 11"
  const winTitle = windowTitle(win) || winLabel;  // "Last 30 days" / "This month" / the dates
  const winPhrase = winTitle.toLowerCase().startsWith("last") || winTitle.toLowerCase().startsWith("this") ? `${winTitle.toLowerCase()} (${winLabel})` : winLabel;
  const e = data.engagement || {};
  const csat = data.csat;
  const sm = SENTIMENT[e.sentiment_label] || SENTIMENT.neutral;
  const score = e.sentiment_score;
  const earlyRead = score != null && e.sentiment_confidence === "low";
  const hasVolume = (data.volume || []).some((d) => d.messages > 0);
  const g = data.glance || {};
  const people = v.lcs("person");

  // ONE window (the range picker), five numbers a manager can act on. Each tile says what it is on hover.
  const glanceCells = [
    { key: "helped", icon: Users, iconCls: "text-redline-500", label: `${v.many("person")} helped`, value: g.residents_helped ?? 0,
      sub: `${g.messages_handled ?? 0} texts & calls handled`, onClick: () => navigate("/dashboard/conversations"),
      help: `Distinct ${people} who texted or called ${winPhrase}.` },
    { key: "ai_alone", icon: Sparkles, iconCls: "text-violet-500", label: "Handled by the AI alone", value: g.ai_alone_pct != null ? `${g.ai_alone_pct}%` : "\u2014",
      sub: g.conversations_active ? `${g.ai_alone} of ${g.conversations_active} · ≈ ${(g.staff_hours_saved ?? 0) >= 10 ? Math.round(g.staff_hours_saved) : g.staff_hours_saved}h staff time saved` : "No conversations yet", onClick: () => navigate("/dashboard/conversations"),
      help: `Conversations ${winPhrase} that never needed a team member — no escalation, no hand-off, no manual reply. Staff time assumes each one would have taken about ${g.minutes_per_contact ?? 15} minutes of a person's day.` },
    { key: "requests", icon: ClipboardList, iconCls: "text-emerald-500", label: "Requests logged → resolved", value: `${g.requests_logged ?? 0} → ${g.requests_resolved ?? 0}`,
      sub: g.requests_open ? `${g.requests_open} still open` : "Nothing left open", onClick: () => navigate("/dashboard/actions"),
      help: `Requests the concierge logged ${winPhrase}, and how many of those are resolved.` },
    { key: "waiting", icon: AlertTriangle, iconCls: "text-amber-500", label: "Waiting on a person now", value: g.waiting_now ?? 0, tone: (g.waiting_now ?? 0) > 0 ? "alert" : undefined,
      sub: `${g.waiting_conversations ?? 0} conversation${g.waiting_conversations === 1 ? "" : "s"} · ${g.waiting_requests ?? 0} urgent request${g.waiting_requests === 1 ? "" : "s"}`,
      onClick: () => navigate((g.waiting_conversations ?? 0) > 0 || !(g.waiting_requests ?? 0) ? "/dashboard/conversations?status=escalated" : "/dashboard/actions"),
      help: "Live right now: conversations waiting on a team member plus urgent or high-priority open requests — the same numbers as the sidebar badges." },
    { key: "teach", icon: HelpCircle, iconCls: "text-blue-500", label: "Couldn\u2019t answer", value: g.couldnt_answer ?? 0,
      sub: g.to_teach ? `${g.to_teach} question${g.to_teach === 1 ? "" : "s"} to teach` : "Nothing waiting to teach", onClick: () => navigate("/dashboard/documents?improve=1"),
      help: `Times ${winPhrase} the AI wasn\u2019t confident enough to answer. Each one is a chance to teach it.` },
  ];

  return (
    <div className="space-y-8 max-w-[1400px]">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className={EYEBROW + " text-redline-600 mb-1.5"}>{v.one("org")} overview</div>
          <h1 className="font-heading text-[22px] font-black tracking-tight text-[#1C1917]">{tenant?.name}</h1>
          <p className="text-[#57534E] mt-1.5 text-sm">
            How <span className="font-semibold text-redline-600">{tenant?.persona_name}</span> is helping your {v.lcs("person")} · <span data-testid="overview-window-label" className="font-semibold text-[#1C1917]">{winTitle}</span>{winTitle !== winLabel ? <span className="text-[#A8A29E]"> · {winLabel}</span> : null}
          </p>
        </div>
        <RangePicker window={win} onChange={setRange} />
      </div>

      {/* Getting Started — phased, module-aware launch journey (auto-hides when done/dismissed) */}
      <GettingStartedCard tenantId={tenantId} onNavigate={navigate} />

      {/* Community Pulse — own community only; never for board members */}
      <PulseCard tenantId={tenantId} tenant={tenant} role={role} />

      {/* Teach banner */}
      {sugCount > 0 && (
        <button data-testid="teach-assistant-banner" onClick={() => navigate("/dashboard/documents?improve=1")}
          className="w-full text-left rounded-xl border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3 hover:bg-[#FEF3C7] transition-colors group">
          <span className="flex items-center gap-2.5 min-w-0 flex-1">
            <Lightbulb className="h-4.5 w-4.5 text-[#B45309] shrink-0" strokeWidth={2} />
            <span className="text-sm min-w-0">
              <span className="font-bold text-[#92400E]">{sugCount} question{sugCount === 1 ? "" : "s"}</span>
              <span className="text-[#B45309]"> your AI couldn't answer — reply once and it learns instantly.</span>
            </span>
          </span>
          <span className="shrink-0 self-end sm:self-auto inline-flex items-center gap-1 text-[#92400E] text-sm font-bold group-hover:gap-2 transition-all">
            Teach the AI <ChevronRight className="h-4 w-4" />
          </span>
        </button>
      )}

      {/* Open-line activity — only when Caller recognition is "Open to anyone" */}
      {tenant?.unknown_caller?.mode === "concierge" && (() => {
        const ba = data.beta_activity || {};
        const pending = ba.pending_approvals || 0;
        const unknown = ba.unknown_conversations_7d || 0;
        const signups = ba.self_registrations_7d || 0;
        const optins = ba.sms_optins_7d || 0;
        return (
          <section data-testid="open-line-activity-card" className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50/70 to-white overflow-hidden">
            <div className="px-5 py-3.5 flex items-center gap-2.5 border-b border-amber-100">
              <Sparkles className="h-4 w-4 text-amber-600" strokeWidth={2} />
              <h2 className="font-heading font-bold text-[#1C1917] text-sm">Open line this week</h2>
              <span className="rounded-full bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide">Open to anyone</span>
              <span className="ml-auto text-[11px] text-[#A8A29E] hidden sm:block">Anyone can call or text — see who&apos;s trying you</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-amber-100">
              <button data-testid="open-line-pending-approvals" onClick={() => navigate("/dashboard/roster?filter=pending")}
                className="px-5 py-4 text-left hover:bg-amber-50/60 transition-colors group">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-amber-700">
                  <UserPlus className="h-3.5 w-3.5" strokeWidth={2} /> Pending approvals
                </div>
                <div className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#1C1917] mt-2 leading-none tabular-nums">{pending}</div>
                <div className="text-[11px] text-[#A8A29E] mt-1 flex items-center gap-1">
                  Review the roster <ChevronRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </button>
              <button data-testid="open-line-unknown-convos" onClick={() => navigate("/dashboard/conversations")}
                className="px-5 py-4 text-left hover:bg-amber-50/60 transition-colors group">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-amber-700">
                  <PhoneCall className="h-3.5 w-3.5" strokeWidth={2} /> Unknown contacts · 7d
                </div>
                <div className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#1C1917] mt-2 leading-none tabular-nums">{unknown}</div>
                <div className="text-[11px] text-[#A8A29E] mt-1 flex items-center gap-1">
                  Open conversations <ChevronRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </button>
              <div className="px-5 py-4">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-amber-700">
                  <Sparkles className="h-3.5 w-3.5" strokeWidth={2} /> New self-registrations · 7d
                </div>
                <div className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#1C1917] mt-2 leading-none tabular-nums">{signups}</div>
                <div className="text-[11px] text-[#A8A29E] mt-1">From call &amp; text</div>
              </div>
              <button data-testid="open-line-sms-optins" onClick={() => navigate("/dashboard/roster?consent=optins&range=week")}
                className="px-5 py-4 text-left hover:bg-amber-50/60 transition-colors group">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-amber-700">
                  <ShieldCheck className="h-3.5 w-3.5" strokeWidth={2} /> SMS opt-ins · 7d
                </div>
                <div className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#1C1917] mt-2 leading-none tabular-nums">{optins}</div>
                <div className="text-[11px] text-[#A8A29E] mt-1 flex items-center gap-1">
                  View consent records <ChevronRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </button>
            </div>
          </section>
        );
      })()}

      {/* Voice + Text pulse — per-community volume (no platform-wide ceiling shown) */}
      {pulse && (() => {
        const v = pulse.voice || {};
        const t = pulse.text || {};
        const fmtCallLen = (s) => s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
        return (
          <section data-testid="pulse-cards" className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Voice pulse */}
            <div data-testid="voice-pulse-card" className="rounded-2xl border border-[#E5E0D8] bg-white overflow-hidden">
              <div className="px-5 py-3.5 flex items-center gap-2.5 border-b border-[#F0EDE6]">
                <span className={`h-8 w-8 rounded-xl flex items-center justify-center ${v.live_now > 0 ? "bg-emerald-50" : "bg-sand-50"}`}>
                  <PhoneCall className={`h-4 w-4 ${v.live_now > 0 ? "text-emerald-600" : "text-ink/40"}`} strokeWidth={1.75} />
                </span>
                <h2 className="font-heading font-bold text-[#1C1917] text-sm">Voice</h2>
                {v.live_now > 0 && (
                  <span data-testid="voice-live-chip" className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" /> {v.live_now} live now
                  </span>
                )}
                <span className="ml-auto text-[11px] text-[#A8A29E]" title="The pulse is always live — today and the last 7 days — no matter which range is picked above">Live pulse · today &amp; last 7 days</span>
              </div>
              <div className="grid grid-cols-3 divide-x divide-[#F0EDE6]">
                <div className="px-5 py-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-[#8A8378]">Calls today</div>
                  <div data-testid="voice-calls-today" className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#1C1917] mt-1 leading-none tabular-nums">{v.calls_today || 0}</div>
                  <div className="text-[11px] text-[#A8A29E] mt-1">{v.minutes_today || 0} min</div>
                </div>
                <div className="px-5 py-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-[#8A8378]">Calls · 7d</div>
                  <div data-testid="voice-calls-7d" className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#1C1917] mt-1 leading-none tabular-nums">{v.calls_7d || 0}</div>
                  <div className="text-[11px] text-[#A8A29E] mt-1">{v.minutes_7d || 0} min total</div>
                </div>
                <div className="px-5 py-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-[#8A8378]">Avg call</div>
                  <div data-testid="voice-avg-call" className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#1C1917] mt-1 leading-none tabular-nums">
                    {v.avg_call_seconds ? fmtCallLen(v.avg_call_seconds) : "—"}
                  </div>
                  <div className="text-[11px] text-[#A8A29E] mt-1">per call</div>
                </div>
              </div>
            </div>

            {/* Text pulse */}
            <div data-testid="text-pulse-card" className="rounded-2xl border border-[#E5E0D8] bg-white overflow-hidden">
              <div className="px-5 py-3.5 flex items-center gap-2.5 border-b border-[#F0EDE6]">
                <span className="h-8 w-8 rounded-xl bg-sand-50 flex items-center justify-center">
                  <MessageSquare className="h-4 w-4 text-ink/40" strokeWidth={1.75} />
                </span>
                <h2 className="font-heading font-bold text-[#1C1917] text-sm">Text</h2>
                <span className="ml-auto text-[11px] text-[#A8A29E]" title="The pulse is always live — today and the last 7 days — no matter which range is picked above">Live pulse · today &amp; last 7 days</span>
              </div>
              <div className="grid grid-cols-3 divide-x divide-[#F0EDE6]">
                <div className="px-5 py-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-[#8A8378]">Messages today</div>
                  <div data-testid="text-msgs-today" className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#1C1917] mt-1 leading-none tabular-nums">{t.messages_today || 0}</div>
                  <div className="text-[11px] text-[#A8A29E] mt-1">inbound</div>
                </div>
                <div className="px-5 py-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-[#8A8378]">Messages · 7d</div>
                  <div data-testid="text-msgs-7d" className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#1C1917] mt-1 leading-none tabular-nums">{t.messages_7d || 0}</div>
                  <div className="text-[11px] text-[#A8A29E] mt-1">inbound</div>
                </div>
                <div className="px-5 py-4">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-[#8A8378]">Active threads · 7d</div>
                  <div data-testid="text-active-threads" className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[#1C1917] mt-1 leading-none tabular-nums">{t.active_threads_7d || 0}</div>
                  <div className="text-[11px] text-[#A8A29E] mt-1">unique</div>
                </div>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Needs attention — urgent requests + escalated threads */}
      {(() => {
        const na = data.needs_attention || {};
        const urg = na.urgent_requests || [];
        const esc = na.escalated_conversations || [];
        if (!urg.length && !esc.length) return null;
        return (
          <section data-testid="needs-attention-section" className="rounded-2xl border border-[#E5E0D8] bg-white overflow-hidden">
            <div className="px-5 py-3.5 flex items-center gap-2.5 border-b border-[#F0EDE6]">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-redline-400 opacity-60" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-redline-500" />
              </span>
              <h2 className="font-heading font-bold text-[#1C1917] text-sm">Needs attention</h2>
              <span className="rounded-full bg-redline-50 text-redline-700 border border-redline-100 px-2 py-0.5 text-[11px] font-bold">{urg.length + esc.length}</span>
              <span className="ml-auto text-[11px] text-[#A8A29E] hidden sm:block">Resolve these first — {V.lcs("person")} are waiting</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 md:divide-x divide-y md:divide-y-0 divide-[#F0EDE6]">
              {urg.length > 0 && (
                <div className="px-5 py-4">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-[#78716C] mb-1.5 flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 text-danger-500" strokeWidth={2} /> Urgent requests
                  </div>
                  {urg.slice(0, 5).map((a) => (
                    <button key={a.id} data-testid={`urgent-request-${a.id}`} onClick={() => navigate(`/dashboard/actions?ticket=${a.id}`)}
                      className="w-full text-left flex items-center justify-between gap-3 py-2.5 border-b border-[#F5F2EC] last:border-0 group rounded-lg -mx-2 px-2 hover:bg-[#FAF8F4] transition-colors">
                      <span className="min-w-0 text-sm">
                        <span className="font-semibold text-[#1C1917] truncate block group-hover:text-redline-700 transition-colors">{a.title || a.summary || a.type}</span>
                        <span className="text-xs text-[#A8A29E]">{[a.ref, homeLabel(a), a.resident].filter(Boolean).join(" · ")}</span>
                      </span>
                      <span className="shrink-0 flex items-center gap-2">
                        <span className="rounded-full bg-danger-50 text-danger-700 border border-danger-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">{a.priority === "urgent" || a.severity ? "Urgent" : "High"}</span>
                        <ChevronRight className="h-4 w-4 text-[#D6D0C6] group-hover:text-redline-500 group-hover:translate-x-0.5 transition-all" />
                      </span>
                    </button>
                  ))}
                  {urg.length > 5 && <div className="text-xs text-[#78716C] font-semibold mt-2">+{urg.length - 5} more in Requests</div>}
                </div>
              )}
              {esc.length > 0 && (
                <div className="px-5 py-4">
                  <div className="text-[11px] font-bold uppercase tracking-wider text-[#78716C] mb-1.5 flex items-center gap-1.5">
                    <MessageSquareWarning className="h-3.5 w-3.5 text-amber-500" strokeWidth={2} /> Escalated conversations
                  </div>
                  {esc.slice(0, 5).map((c) => (
                    <button key={c.id} data-testid={`escalated-conv-${c.id}`} onClick={() => navigate(`/dashboard/conversations?status=escalated&open=${c.id}`)}
                      className="w-full text-left flex items-center justify-between gap-3 py-2.5 border-b border-[#F5F2EC] last:border-0 group rounded-lg -mx-2 px-2 hover:bg-[#FAF8F4] transition-colors">
                      <span className="min-w-0 flex-1 text-sm">
                        <span className="font-semibold text-[#1C1917] truncate block group-hover:text-redline-700 transition-colors">{c.active_person_name || c.phone}</span>
                        <span className="text-xs text-[#A8A29E] capitalize line-clamp-2">{(c.escalation_reason || "needs a human").replace(/_/g, " ")}{c.channel ? ` · ${c.channel}` : ""}</span>
                      </span>
                      <span className="shrink-0 flex items-center gap-2">
                        <span className="rounded-full bg-amber-50 text-amber-700 border border-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">Escalated</span>
                        <ChevronRight className="h-4 w-4 text-[#D6D0C6] group-hover:text-redline-500 group-hover:translate-x-0.5 transition-all" />
                      </span>
                    </button>
                  ))}
                  {esc.length > 5 && <div className="text-xs text-[#78716C] font-semibold mt-2">+{esc.length - 5} more in Conversation History</div>}
                </div>
              )}
            </div>
          </section>
        );
      })()}

      {/* One row, one window — the range picked above */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <h2 className={EYEBROW} data-testid="glance-window-title">{winTitle}{winTitle !== winLabel ? <span className="normal-case tracking-normal font-medium text-[#A8A29E]"> · {winLabel}</span> : null}</h2>
          <span className="h-px flex-1 bg-[#E5E0D8]" />
          <span className="text-[11px] text-[#A8A29E] hidden sm:block">Hover a tile to see how it&apos;s counted · click to open</span>
        </div>
        <MetricStrip cols="lg:grid-cols-5" cells={glanceCells} testidPrefix="stat-" />
      </section>

      {/* Chart (dominant) + Sentiment + CSAT */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className={`lg:col-span-8 ${CARD} p-6`}>
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-4 w-4 text-redline-500" strokeWidth={2} />
            <h3 className="font-heading font-bold text-[#1C1917]">Inbound texts &amp; calls</h3>
            <span className="text-[11px] text-[#A8A29E]" data-testid="volume-window-label">· {winLabel}{(data.volume || [])[0]?.weekly ? " · by week" : " · by day"}</span>
            <div className="ml-auto flex items-center gap-4 text-[11px] font-semibold text-[#78716C]" data-testid="volume-legend">
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: accent }} /> Texts</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: CALLS_COLOR }} /> Calls</span>
            </div>
          </div>
          {hasVolume ? (
            <ResponsiveContainer width="100%" height={230}>
              <AreaChart data={data.volume} margin={{ top: 8, right: 6, left: -6, bottom: 0 }}>
                <defs>
                  <linearGradient id="msgFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={accent} stopOpacity={0.16} />
                    <stop offset="100%" stopColor={accent} stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="callFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CALLS_COLOR} stopOpacity={0.08} />
                    <stop offset="100%" stopColor={CALLS_COLOR} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="4 4" stroke={LINE} vertical={false} />
                <XAxis dataKey="day" tickLine={false} axisLine={false} tick={{ fontSize: 12, fill: "#A8A29E" }} dy={6} interval="preserveStartEnd" minTickGap={18} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#A8A29E" }} width={44} />
                <Tooltip cursor={{ stroke: accent, strokeWidth: 1, strokeDasharray: "4 4" }} content={<ChartTooltip />} />
                <Area type="monotone" dataKey="texts" name="Texts" stroke={accent} strokeWidth={2.5} fill="url(#msgFill)"
                  dot={{ r: 3, fill: accent, strokeWidth: 0 }} activeDot={{ r: 5, fill: accent, stroke: "#fff", strokeWidth: 2 }} />
                <Area type="monotone" dataKey="calls" name="Calls" stroke={CALLS_COLOR} strokeWidth={2} strokeDasharray="5 4" fill="url(#callFill)"
                  dot={{ r: 3, fill: CALLS_COLOR, strokeWidth: 0 }} activeDot={{ r: 5, fill: CALLS_COLOR, stroke: "#fff", strokeWidth: 2 }} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[230px] flex flex-col items-center justify-center text-center gap-3 rounded-xl border-2 border-dashed border-[#E5E0D8] bg-[#F9F8F6]/50">
              <MessageSquareDashed className="h-11 w-11 text-[#D6D3D1]" strokeWidth={1.25} />
              <p className="text-sm text-[#78716C] max-w-xs">No texts or calls {winLabel} yet. {V.one("person")} activity will chart here.</p>
            </div>
          )}
          <div className="mt-5">
            <AskedAndAfterHours topics={data.topics} afterHours={data.after_hours} people={people} />
          </div>
        </div>

        <div className="lg:col-span-4 space-y-5">
          {/* Sentiment (rainbow) */}
          <div data-testid="sentiment-card" className={`${CARD} p-6`}>
            <div className="flex items-center gap-2 mb-3">
              <Gauge className="h-4 w-4 text-redline-500" strokeWidth={2} />
              <h3 className="font-heading font-bold text-[#1C1917] text-sm">{v.one("person")} sentiment</h3>
              {SENT_BASIS[e.sentiment_basis] && (
                <span data-testid="sentiment-basis-chip" title={SENT_BASIS[e.sentiment_basis].title}
                  className="ml-auto text-[10px] font-bold uppercase tracking-wide rounded-full bg-violet-50 text-violet-700 border border-violet-200 px-2 py-0.5">{SENT_BASIS[e.sentiment_basis].chip}</span>
              )}
              <button type="button" data-testid="sentiment-explainer-toggle" onClick={() => setSentHelp((x) => !x)} aria-expanded={sentHelp}
                className={`${SENT_BASIS[e.sentiment_basis] ? "" : "ml-auto "}text-[11px] font-semibold text-[#8A8378] hover:text-[#1C1917] inline-flex items-center gap-1`}>
                <HelpCircle className="h-3.5 w-3.5" /> What is this?
              </button>
            </div>
            {sentHelp && (
              <div data-testid="sentiment-explainer" className="mb-4 rounded-xl bg-[#F9F8F6] border border-[#EDE8E0] p-3 text-[12px] leading-relaxed text-[#57534E]">
                <b className="text-[#1C1917]">How this number is made.</b> Every finished phone call is read by the AI for two things — did the caller get what they needed (yes · partial · no) and how did they sound by the end (satisfied · neutral · frustrated). Texts are read for positive and negative wording. Calls and texts are blended by how many of each came in over the range you picked, so one call can't swing the gauge on its own. A <i>partial</i> answer counts as half a win. Under 5 signals it's an <i>early read</i>, not a verdict.
              </div>
            )}
            <div className="flex items-end justify-between">
              <div className="flex items-end gap-1">
                <span className="font-heading text-4xl font-black tracking-tighter text-[#1C1917] leading-none">{score != null ? score : "\u2014"}</span>
                {score != null && <span className="text-[#A8A29E] mb-1 text-sm font-bold">/100</span>}
              </div>
              {earlyRead ? (
                <div data-testid="sentiment-early-read" className="text-xs font-bold flex items-center gap-1.5 text-[#8A8378]" title={`Fewer than 5 scored calls and texts ${winPhrase} — treat this as a hint, not a verdict`}>
                  <span className="h-2 w-2 rounded-full bg-sand-300" /> Early read · {e.sentiment_signals} signal{e.sentiment_signals === 1 ? "" : "s"}
                </div>
              ) : (
                <div data-testid="sentiment-label" className={`text-xs font-bold flex items-center gap-1.5 ${sm.cls}`}>
                  <span className={`h-2 w-2 rounded-full ${sm.dot}`} /> {sm.label}
                </div>
              )}
            </div>
            <div className="mt-7 mb-1">
              <div className="relative h-3 rounded-full bg-[linear-gradient(90deg,#D32F2F_0%,#F59E0B_50%,#10B981_100%)]">
                {score != null && (
                  <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 transition-all duration-700 ease-out" style={{ left: `${Math.min(100, Math.max(0, score))}%` }}>
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-lg bg-[#1C1917] text-white text-[11px] font-bold px-2 py-1 shadow-md">
                      {score}
                      <span className="absolute left-1/2 -bottom-1 -translate-x-1/2 h-2 w-2 rotate-45 bg-[#1C1917]" />
                    </div>
                    <div className={`h-5 w-5 rounded-full bg-white shadow-[0_2px_8px_rgba(0,0,0,0.28)] ring-4 ${earlyRead ? "ring-sand-300/40" : sm.ring} flex items-center justify-center`}>
                      <span className={`h-2 w-2 rounded-full ${earlyRead ? "bg-sand-300" : sm.dot}`} />
                    </div>
                  </div>
                )}
              </div>
              <div className="flex justify-between text-[10px] font-semibold uppercase tracking-wide text-[#A8A29E] mt-3">
                <span>Needs attention</span><span>Positive</span>
              </div>
              {e.sentiment_basis_text && (
                <div data-testid="sentiment-basis-line" className="text-[11px] text-[#78716C] mt-2">{e.sentiment_basis_text}</div>
              )}
              {(data.call_quality?.scored_calls_30d ?? 0) > 0 && (
                <div data-testid="call-quality-line" className="text-[11px] text-[#A8A29E] mt-1">
                  {data.call_quality.need_met_pct != null
                    ? `${data.call_quality.need_met_pct}% of callers got what they needed`
                    : `Needs-met % shows after ${data.call_quality.need_met_min_calls ?? 3} scored calls`}
                </div>
              )}
            </div>
          </div>

          {/* Resolution split — AI vs a person */}
          {(data.resolved_conversations ?? 0) > 0 && (() => {
            const ai = data.resolved_by_ai ?? 0;
            const person = data.resolved_by_person ?? 0;
            const total = ai + person;
            const aiPct = total ? Math.round((ai / total) * 100) : 0;
            return (
              <div data-testid="resolution-split-card" className={`${CARD} p-6`}>
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 className="h-4 w-4 text-redline-500" strokeWidth={2} />
                  <h3 className="font-heading font-bold text-[#1C1917] text-sm">How calls &amp; texts get resolved</h3>
                  <span className="ml-auto text-[11px] text-[#A8A29E]">{winLabel}</span>
                </div>
                <div className="flex items-end justify-between">
                  <div className="flex items-end gap-1">
                    <span data-testid="resolved-ai-pct" className="font-heading text-4xl font-black tracking-tighter text-[#1C1917] leading-none">{aiPct}</span>
                    <span className="text-[#A8A29E] mb-1 text-sm font-bold">% by AI</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="flex items-center gap-1 text-violet-600 font-bold"><Sparkles className="h-3.5 w-3.5" /> {ai}</span>
                    <span className="flex items-center gap-1 text-[#78716C] font-bold"><Users className="h-3.5 w-3.5" /> {person}</span>
                  </div>
                </div>
                <div className="mt-3 h-2.5 rounded-full bg-sand-100 overflow-hidden flex">
                  <div className="h-full bg-violet-500 transition-all duration-700" style={{ width: `${aiPct}%` }} />
                  <div className="h-full bg-[#A8A29E] transition-all duration-700" style={{ width: `${100 - aiPct}%` }} />
                </div>
                <div className="text-[11px] text-[#A8A29E] mt-2">
                  <b className="text-violet-600">{ai}</b> resolved by the AI on its own · <b>{person}</b> by a person · {total} total
                </div>
              </div>
            );
          })()}

          {/* CSAT (compact) */}
          {csat && (
            <div data-testid="csat-card" className={`${CARD} p-6`}>
              <div className="flex items-center gap-2 mb-3">
                <ThumbsUp className="h-4 w-4 text-redline-500" strokeWidth={2} />
                <h3 className="font-heading font-bold text-[#1C1917] text-sm">{v.one("person")} satisfaction</h3>
                <span className="ml-auto text-[11px] text-[#A8A29E]">{winLabel}</span>
              </div>
              {csat.total > 0 ? (
                <>
                  <div className="flex items-end justify-between">
                    <div className="flex items-end gap-1">
                      <span data-testid="csat-score" className="font-heading text-4xl font-black tracking-tighter text-[#1C1917] leading-none">{csat.score}</span>
                      <span className="text-[#A8A29E] mb-1 text-sm font-bold">%</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="flex items-center gap-1 text-emerald-600 font-bold"><ThumbsUp className="h-3.5 w-3.5" /> {csat.up}</span>
                      <span className="flex items-center gap-1 text-danger-600 font-bold"><ThumbsDown className="h-3.5 w-3.5" /> {csat.down}</span>
                    </div>
                  </div>
                  <div className="mt-3 h-2.5 rounded-full bg-sand-100 overflow-hidden flex">
                    <div className="h-full bg-emerald-500 transition-all duration-700" style={{ width: `${csat.score}%` }} />
                    <div className="h-full bg-redline-500 transition-all duration-700" style={{ width: `${100 - csat.score}%` }} />
                  </div>
                  <div className="text-[11px] text-[#A8A29E] mt-2">
                    {csat.total} rating{csat.total === 1 ? "" : "s"}
                    {((csat.voice?.up ?? 0) + (csat.voice?.down ?? 0)) > 0 && (
                      <span data-testid="csat-channel-split"> · {(csat.voice.up + csat.voice.down)} by phone, {(csat.text?.up ?? 0) + (csat.text?.down ?? 0)} by text</span>
                    )}
                    {csat.pending > 0 ? ` · ${csat.pending} awaiting reply` : ""}
                  </div>
                  {(csat.ai_resolved?.total ?? 0) > 0 && (
                    <div data-testid="csat-ai-pulse" className="mt-2 pt-2 border-t border-sand-100 text-[11px] text-[#78716C] flex items-center gap-1.5">
                      <Sparkles className="h-3 w-3 text-violet-500 shrink-0" />
                      <span>On AI-resolved chats: <b className="text-emerald-600">{csat.ai_resolved.up} 👍</b> · <b className="text-danger-600">{csat.ai_resolved.down} 👎</b>{csat.ai_resolved.score != null ? ` · ${csat.ai_resolved.score}%` : ""}</span>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-[#78716C]">No ratings {winLabel}. {V.many("person")} rate 👍/👎 on resolved chats and at the end of phone calls.{csat.pending > 0 ? ` ${csat.pending} awaiting a reply.` : ""}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Lists */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className={`${CARD} p-6`}>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" strokeWidth={2} />
            <h3 className="font-heading font-bold text-[#1C1917] text-sm">Why {v.lcs("person")} escalated</h3>
            <span className="ml-auto text-[11px] text-[#A8A29E]">{winLabel}</span>
          </div>
          {Object.keys(data.escalation_reasons).length === 0 ? (
            <p className="text-sm text-[#A8A29E] py-3">No escalations {winLabel}.</p>
          ) : (() => {
            const entries = Object.entries(data.escalation_reasons).sort((a, b) => b[1] - a[1]);
            const shown = showAllEsc ? entries : entries.slice(0, CAP);
            return (<>
              {shown.map(([r, n]) => (
                <div key={r} className="flex items-center justify-between py-3 border-b border-[#EDE8E0] last:border-0 text-sm">
                  <span className="capitalize text-[#57534E]">{r.replace(/_/g, " ")}</span>
                  <span className="font-black text-redline-600">{n}</span>
                </div>
              ))}
              {entries.length > CAP && (
                <button onClick={() => setShowAllEsc((v) => !v)} className="mt-2 text-xs font-semibold text-redline-600 hover:text-redline-700">
                  {showAllEsc ? "Show less" : `+${entries.length - CAP} more`}
                </button>
              )}
            </>);
          })()}
        </div>
        <div className={`${CARD} p-6`}>
          <div className="flex items-center gap-2 mb-2">
            <ClipboardList className="h-4 w-4 text-emerald-500" strokeWidth={2} />
            <h3 className="font-heading font-bold text-[#1C1917] text-sm">Requests by type</h3>
            <span className="ml-auto text-[11px] text-[#A8A29E]">{winLabel}</span>
          </div>
          {Object.keys(data.actions_by_type).length === 0 ? (
            <p className="text-sm text-[#A8A29E] py-3">No requests {winLabel}.</p>
          ) : (() => {
            const entries = Object.entries(data.actions_by_type).sort((a, b) => b[1] - a[1]);
            const shown = showAllReq ? entries : entries.slice(0, CAP);
            return (<>
              {shown.map(([r, n]) => (
                <div key={r} className="flex items-center justify-between py-3 border-b border-[#EDE8E0] last:border-0 text-sm">
                  <span className="capitalize text-[#57534E]">{r.replace(/_/g, " ")}</span>
                  <span className="font-black text-[#1C1917]">{n}</span>
                </div>
              ))}
              {entries.length > CAP && (
                <button onClick={() => setShowAllReq((v) => !v)} className="mt-2 text-xs font-semibold text-redline-600 hover:text-redline-700">
                  {showAllReq ? "Show less" : `+${entries.length - CAP} more`}
                </button>
              )}
            </>);
          })()}
        </div>
      </div>

      {/* What the concierge knows — roster + documents, kept on their own pages */}
      <div data-testid="overview-setup-line" className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[#8A8378] pt-2 border-t border-[#EDE8E0]">
        <span className="font-semibold uppercase tracking-wider text-[10px]">On file</span>
        <button type="button" data-testid="setup-roster-link" onClick={() => navigate("/dashboard/roster")} className="hover:text-[#1C1917] underline-offset-2 hover:underline">
          {data.residents ?? 0} {(data.residents === 1 ? v.one("person") : v.many("person")).toLowerCase()} in {data.households ?? 0} {(data.households === 1 ? v.one("unit") : v.many("unit")).toLowerCase()}
        </button>
        <span aria-hidden>·</span>
        <button type="button" data-testid="setup-documents-link" onClick={() => navigate("/dashboard/documents")} className="hover:text-[#1C1917] underline-offset-2 hover:underline">
          {data.documents ?? 0} AI document{data.documents === 1 ? "" : "s"}
        </button>
      </div>
    </div>
  );
}
